import type { RichText, RichTextAlign } from '@jobos/shared';

// Pure conversion between our stored RichText (blocks + runs) and Tiptap's
// ProseMirror JSON document. Kept dependency-free so it's unit-testable without
// mounting an editor. Block-level align/fontSize map to node attrs; bold/italic/
// underline map to text-node marks. 'left' is the default and is not stored.

export interface TiptapMark {
  type: string;
}
export interface TiptapTextNode {
  type: 'text';
  text?: string;
  marks?: TiptapMark[];
}
export interface TiptapBlockNode {
  type: string; // 'paragraph' | 'heading'
  attrs?: { level?: number; textAlign?: string | null; fontSize?: number | null };
  content?: TiptapTextNode[];
}
export interface TiptapDoc {
  type: 'doc';
  content?: TiptapBlockNode[];
}

function marksForRun(run: RichText[number]['runs'][number]): TiptapMark[] {
  const marks: TiptapMark[] = [];
  if (run.bold) marks.push({ type: 'bold' });
  if (run.italic) marks.push({ type: 'italic' });
  if (run.underline) marks.push({ type: 'underline' });
  return marks;
}

export function richTextToTiptapDoc(value: RichText): TiptapDoc {
  const content: TiptapBlockNode[] = value.map((block) => {
    // Body text only — headings were removed, so any legacy heading block is
    // rendered (and re-saved) as a paragraph.
    const attrs: TiptapBlockNode['attrs'] = {};
    if (block.align && block.align !== 'left') attrs.textAlign = block.align;
    if (block.fontSize) attrs.fontSize = block.fontSize;

    const texts: TiptapTextNode[] = block.runs
      .filter((r) => r.text.length > 0)
      .map((r) => {
        const node: TiptapTextNode = { type: 'text', text: r.text };
        const marks = marksForRun(r);
        if (marks.length) node.marks = marks;
        return node;
      });

    const node: TiptapBlockNode = { type: 'paragraph', attrs };
    if (texts.length) node.content = texts;
    return node;
  });

  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph', attrs: {} }] };
}

export function tiptapDocToRichText(doc: TiptapDoc): RichText {
  return (doc.content ?? []).map((node) => {
    const block: RichText[number] = {
      style: 'body',
      runs: (node.content ?? [])
        .filter((n) => n.type === 'text')
        .map((n) => {
          const run: RichText[number]['runs'][number] = { text: n.text ?? '' };
          for (const m of n.marks ?? []) {
            if (m.type === 'bold') run.bold = true;
            if (m.type === 'italic') run.italic = true;
            if (m.type === 'underline') run.underline = true;
          }
          return run;
        }),
    };
    const align = node.attrs?.textAlign;
    if (align && align !== 'left') block.align = align as RichTextAlign;
    if (node.attrs?.fontSize) block.fontSize = node.attrs.fontSize;
    return block;
  });
}
